import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {
  GetHeaderV2Response_TargetGroupItem,
  GetHeaderV2Response_TargetGroupSwitch,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  darkTheme,
} from '@aboutyou/ui-components';
import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {Link} from '../../../features/Link/Link';
import {useSelectedTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';

interface BrandPageGenderSwitchProps {
  className?: string;
  targetGroups: GetHeaderV2Response_TargetGroupItem[];
  targetGroupSwitch?: GetHeaderV2Response_TargetGroupSwitch;
}

const BrandPageGenderSwitchView: React.FC<
  BrandPageGenderSwitchProps & {
    activeTargetGroup: TargetGroup;
    onSelectTargetGroup: (targetGroup: TargetGroup) => void;
  }
> = ({
  className,
  targetGroups = [],
  activeTargetGroup,
  onSelectTargetGroup,
}) => {
  if (targetGroups.length < 2) {
    return null;
  }

  return (
    <section className={className} data-testid="brandGenderSwitch">
      {targetGroups.map(element => (
        <StyledGender
          key={element.targetGroup}
          label={element.label}
          linkTarget={element.shopPath}
          data-active={element.targetGroup === activeTargetGroup}
          onClick={() => onSelectTargetGroup(element.targetGroup)}
        />
      ))}
    </section>
  );
};

export const DesktopBrandPageGenderSwitch: React.FC<
  BrandPageGenderSwitchProps
> = ({className, targetGroups, targetGroupSwitch}) => {
  const {
    targetGroup: cookieTargetGroup,
    initialTargetGroup: cookieInitialTargetGroup,
  } = useSelectedTargetGroup();
  const [activeTargetGroup, setActiveTargetGroup] = useState<TargetGroup>(
    () => targetGroupSwitch?.targetGroup ?? cookieTargetGroup,
  );

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    if (cookieInitialTargetGroup === cookieTargetGroup) {
      setActiveTargetGroup(cookieTargetGroup);
    }
  }, [cookieInitialTargetGroup, cookieTargetGroup]);

  return (
    <BrandPageGenderSwitchView
      className={className}
      targetGroups={targetGroups}
      activeTargetGroup={activeTargetGroup}
      onSelectTargetGroup={setActiveTargetGroup}
    />
  );
};

// When switching gender via offcanvas (mobile flow),
// gender-switch widget on Brand page should not sync its value to keep consistent page stream
export const MobileBrandPageGenderSwitch: React.FC<
  BrandPageGenderSwitchProps
> = ({className, targetGroups, targetGroupSwitch}) => {
  const {
    targetGroup: cookieTargetGroup,
    initialTargetGroup: cookieInitialTargetGroup,
  } = useSelectedTargetGroup();
  const hasUpdatedOnMount = useRef(false);
  const activeTargetGroup = useRef<TargetGroup>(
    targetGroupSwitch?.targetGroup ?? cookieTargetGroup,
  );

  useEffect(() => {
    if (
      !hasUpdatedOnMount.current &&
      cookieInitialTargetGroup === cookieTargetGroup
    ) {
      hasUpdatedOnMount.current = true;
      activeTargetGroup.current = cookieTargetGroup;
    }
  }, [cookieInitialTargetGroup, cookieTargetGroup]);

  return (
    <BrandPageGenderSwitchView
      className={className}
      targetGroups={targetGroups}
      activeTargetGroup={activeTargetGroup.current}
      onSelectTargetGroup={targetGroup => {
        activeTargetGroup.current = targetGroup;
      }}
    />
  );
};

const StyledGender = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  min-width: 52px;
  height: 100%;
  box-sizing: content-box;
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 18px;
  transition: color 0.5s;
  text-decoration: none;
  text-transform: uppercase;

  ${darkTheme(css`
    color: ${COLORS.Grey2};
  `)}

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    margin: auto;
    width: 24px;
    height: 2px;
    background: ${COLORS.Black};
    transform: scale(0);
    transition: transform 0.5s ease;

    ${darkTheme(css`
      background: ${COLORS.White};
    `)}
  }

  &[data-active='true'],
  :hover {
    color: ${COLORS.Black};
    font-weight: ${FONT_WEIGHTS.w700};

    &::after {
      transform: scale(1);
    }

    ${darkTheme(css`
      color: ${COLORS.White};
    `)}
  }

  &:hover {
    color: ${COLORS.Black};

    ${darkTheme(css`
      color: ${COLORS.White};
    `)}
  }
`;
